import { Button, Flex, Form, Input, Divider, Checkbox, message } from "antd";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { getUserInfoByEmail } from "../../services/WebServices";
import VeoliaLogo from "../layout/assets/VeoliaLogo";
import { showLoader } from "../../utils/showloader";
import { useEffect, useState } from "react";
import { ArrowLeftOutlined, CheckCircleFilled, CheckCircleOutlined } from "@ant-design/icons";
import RegistrationSteps from "./RegistrationSteps";
import { loginUser, sendVerifyMail, userConfirmationVerification, userSignup } from "../../services/RegistrationServices";
import { useSearchParams, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserDetail } from "../../redux/slices/userSlice";
import { useTenant } from "../../context/TenantContext.jsx";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { trimStringValues } from "../../utils/trimStrings";
import { normalizeCompanyIdentifier, getStoredCompanyIdentifier, setStoredCompanyIdentifier, clearAuthStorage } from "../../utils/companyIdentifier";
import { useTranslation } from 'react-i18next';

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 }, // Adjust the span for width
};

const LoginPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [registerForm] = Form.useForm();
  const dispatch = useDispatch();

  const [currentState, setCurrentState] = useState("login")
  const [currentStep, setCurrentStep] = useState(0)
  // Removed: Form.useWatch([], registerForm) - was causing infinite re-renders
  // Now using registerForm.getFieldsValue() when needed
  const [registerObj, setRegisterObj] = useState({})
  const [searchParams] = useSearchParams()
  const navigate = useTenantNavigation();
  const { tenantName: urlTenantName } = useParams();
  const { setTenant, themeColor, companyLogo, companyName, fetchCompanyDetails } = useTenant();
  const verifiedEmail = searchParams.get("email")
  const info = searchParams.get("info")
  const [userExists, setUserExists] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState('standard')
  const [userDetail, setUserDetailLocal] = useState({})
  const [isAuthStateLoading, setIsAuthStateLoading] = useState(true)
  const isIdentifierStep = !urlTenantName;

  // Track if custom logo failed to load
  const [logoError, setLogoError] = useState(false)

  // Only redirect to URLs on the same origin to prevent open redirect attacks
  const safeRedirect = (url) => {
    try {
      const parsed = new URL(url, window.location.origin)
      if (parsed.origin === window.location.origin) {
        window.location.href = parsed.pathname + parsed.hash
      }
    } catch {
      // Invalid URL — ignore
    }
  }

  const handleLocalClear = () => {
    clearAuthStorage();
  }

  const verifyUserConfirmation = async () => {
    showLoader(true)
    const verifyResponse = await userConfirmationVerification(verifiedEmail, info)
    showLoader(false)
    if (verifyResponse.status === 200) {
      setRegisterObj({ email: verifiedEmail })
      registerForm.setFieldsValue({ email: verifiedEmail })
      setCurrentStep(2)
    } else {
      message.error(verifyResponse.message)
    }
  }

  // Redirect already-logged-in users to the landing page
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    try {
      const loginData = JSON.parse(localStorage.getItem("loginResponse") || "{}");
      if (loginData?.isAdmin) {
        navigate("/app/dashboard", { replace: true });
      } else {
        navigate("/app/dashboard", { replace: true });
      }
    } catch {
      navigate("/app/dashboard", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (verifiedEmail && info) {
      setCurrentState("register")
      setCurrentStep(1)
      verifyUserConfirmation();
    }
  }, [searchParams])

  useEffect(() => {
    if (!urlTenantName) {
      setIsAuthStateLoading(false);
      return;
    }

    // If already authenticated, redirect instead of clearing the session
    const existingToken = localStorage.getItem("accessToken");
    if (existingToken) {
      navigate("/app/dashboard", { replace: true });
      return;
    }

    const identifier = normalizeCompanyIdentifier(urlTenantName);

    // Check if tenant changed from what's cached
    const cachedTenant = getStoredCompanyIdentifier();

    if (cachedTenant && cachedTenant !== identifier) {
      // Tenant changed! Clear EVERYTHING
      sessionStorage.clear();
      handleLocalClear()
    }

    // Store current tenant
    setStoredCompanyIdentifier(identifier);

    // Fetch company details (will use cache if exists for this tenant)
    fetchCompanyDetails(identifier);
    setIsAuthStateLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTenantName])

  const handleIdentifierContinue = async () => {
    try {
      const { identifier } = await form.validateFields(["identifier"]);
      const normalizedIdentifier = normalizeCompanyIdentifier(identifier);

      if (!normalizedIdentifier) {
        return;
      }

      // Validate the company exists before navigating to the login screen
      showLoader(true);
      const companyResult = await fetchCompanyDetails(normalizedIdentifier);
      showLoader(false);

      if (!companyResult?.success) {
        message.error(companyResult?.message || "Company not found. Please check your identifier.");
        return;
      }

      setStoredCompanyIdentifier(normalizedIdentifier);
      navigate(`/${normalizedIdentifier}/login`);
    } catch (error) {
      return error;
    }
  }

  // Removed: useEffect that synced formValues to registerObj on every change
  // This was causing unnecessary re-renders. Now syncing values only when needed (on step change)

  const responseMessage = async (tokenResponse) => {
    const decoded = jwtDecode(tokenResponse.credential);
    localStorage.setItem("userEmail", decoded.email)
    showLoader(true)

    // Login payload - tenantName is sent via companyIdentifier header
    const loginPayload = {
      email: decoded.email
    };

    // companyIdentifier header is added automatically by axios interceptor
    // The Google ID token travels as Authorization so the backend can verify it
    // rather than trusting the email in the payload.
    let loginResponse = await loginUser(loginPayload, true, tokenResponse.credential)
    showLoader(false)
    // Expired password: no session is established, send the user to reset it.
    if (loginResponse.passwordExpired) {
      handlePasswordExpired(decoded.email, loginResponse.message)
      return
    }
    if (loginResponse.success) {
      showLoader(true)
      let currentUserInfo = await getUserInfoByEmail();
      showLoader(false)
      if (localStorage.getItem("redirectUrl")) {
        let reqUrl = localStorage.getItem("redirectUrl");
        localStorage.removeItem("redirectUrl")
        safeRedirect(reqUrl)
        return
      }
      if (currentUserInfo && currentUserInfo.isAdmin) {
        // Dispatch user info to Redux before navigating
        dispatch(setUserDetail(currentUserInfo));

        if (localStorage.getItem("redirectUrl")) {
          let reqUrl = localStorage.getItem("redirectUrl");
          localStorage.removeItem("redirectUrl")
          safeRedirect(reqUrl)
          return
        }
        // Handle tenant context for Google login
        if (urlTenantName) {
          const tenantResult = await setTenant(urlTenantName);
          if (tenantResult.success) {
            sessionStorage.setItem("tenantId", currentUserInfo.tenantId || tenantResult.data.tenantId);
            navigate("/app/dashboard");
          } else {
            message.error(t('Invalid tenant. Please contact support.'));
            handleLocalClear();
          }
        } else {
          navigate("/app/dashboard");
        }
      } else if (currentUserInfo && currentUserInfo?.email) {
        // Dispatch user info to Redux before navigating
        dispatch(setUserDetail(currentUserInfo));

        // Handle tenant context for Google login
        if (urlTenantName) {
          const tenantResult = await setTenant(urlTenantName);
          if (tenantResult.success) {
            sessionStorage.setItem("tenantId", currentUserInfo.tenantId || tenantResult.data.tenantId);
            navigate("/app/dashboard");
          } else {
            message.error(t('Invalid tenant. Please contact support.'));
            handleLocalClear()
          }
        } else {
          navigate("/app/dashboard");
        }
      } else {
        // Logged in, but no profile exists for this email: drop the half-built
        // session so the user stays on login instead of bouncing to dashboard.
        const savedIdentifier = getStoredCompanyIdentifier();
        handleLocalClear()
        if (savedIdentifier) {
          setStoredCompanyIdentifier(savedIdentifier);
        }
        message.error(currentUserInfo?.message || "User not found")
        return
      }
    } else {
      // Check for tenant mismatch
      if (loginResponse.actualTenantName && normalizeCompanyIdentifier(loginResponse.actualTenantName) !== normalizeCompanyIdentifier(urlTenantName)) {
        const actualTenant = normalizeCompanyIdentifier(loginResponse.actualTenantName);
        message.info(`Redirecting to your tenant: ${actualTenant}`);
        navigate(`/${actualTenant}/login`, { replace: true });
      } else {
        message.error(loginResponse?.message)
      }
    }
  };

  const errorMessage = (error) => {
  };

  const sendVerificationEmail = async (email) => {
    showLoader(true)
    let emailSent = await sendVerifyMail(email)
    showLoader(false)
    if (emailSent.success) {
      registerForm.setFieldValue({ email: email })
      setRegisterObj({ email: email })
      setCurrentStep(1)
      message.success(t('A verification link sent to your email.'))
    } else {
      message.error(emailSent.message)
    }
  }

  const completeSignup = async (signupData) => {
    showLoader(true)
    // Use passed data or fall back to registerObj
    let emailSent = await userSignup(signupData || registerObj)
    showLoader(false)
    if (emailSent.success) {
      setCurrentStep(3)
      setUserDetailLocal(emailSent.data?.result)
    } else {
      message.error(emailSent.message)
    }
  }

  const handleStep = async (e, val) => {
    e.preventDefault();
    // Get current form values on demand instead of watching them
    const currentFormValues = registerForm.getFieldsValue();
    const updatedRegisterObj = { ...registerObj, ...currentFormValues };
    setRegisterObj(updatedRegisterObj);

    if (val === 1) {
      await sendVerificationEmail(updatedRegisterObj.email)
      return
    }
    if (val === 3) {
      await registerForm.validateFields();
      // Pass the latest values to completeSignup
      await completeSignup(updatedRegisterObj);
      return
    }
    setCurrentStep(val)
  }

  // An expired password is not a usable session: drop anything the login call
  // may have cached, warn the user, then hand off to the reset flow with the
  // email prefilled so they only have to request an OTP.
  const handlePasswordExpired = (email, apiMessage) => {
    const savedIdentifier = getStoredCompanyIdentifier();
    handleLocalClear()
    if (savedIdentifier) {
      setStoredCompanyIdentifier(savedIdentifier);
    }

    message.warning(apiMessage || "Your password has expired. Please set a new password to continue.", 5)
    navigate("/forgot-password", { state: { email, passwordExpired: true } })
  }

  const handleBackToLogin = () => {
    setCurrentState("login")
    setCurrentStep(0)
    form.resetFields();
    setShowSignup(false)
  }

  const handleLogin = async () => {
    let formFields = trimStringValues(form.getFieldsValue(), ["password"]);
    try {
      showLoader(true)

      // Login payload - tenantName is sent via companyIdentifier header
      const loginPayload = {
        ...formFields
      };

      // companyIdentifier header is added automatically by axios interceptor
      let loginResponse = await loginUser(loginPayload, false)
      showLoader(false)

      // Expired password: no session is established, send the user to reset it.
      if (loginResponse.passwordExpired) {
        handlePasswordExpired(loginPayload.email, loginResponse.message)
        return
      }

      if (loginResponse.success) {
        // Store user data in localStorage
        localStorage.setItem('userEmail', loginPayload.email);
        localStorage.setItem("accessToken", loginResponse.data?.accessToken)
        localStorage.setItem("loginResponse", JSON.stringify(loginResponse.data))

        // Fetch full user info and dispatch to Redux (matching Google login flow)
        showLoader(true)
        let currentUserInfo = await getUserInfoByEmail();
        showLoader(false)

        if (localStorage.getItem("redirectUrl")) {
          let reqUrl = localStorage.getItem("redirectUrl");
          localStorage.removeItem("redirectUrl")
          safeRedirect(reqUrl)
          return
        }

        if (currentUserInfo && currentUserInfo.isAdmin) {
          const profileId = currentUserInfo?.userId || currentUserInfo?.id || "";
          if (profileId) {
            localStorage.setItem("userId", profileId);
            localStorage.setItem("id", profileId);
          }
          // Dispatch user info to Redux before navigating
          dispatch(setUserDetail(currentUserInfo));

          // Handle tenant context
          if (urlTenantName) {
            const tenantResult = await setTenant(urlTenantName);
            if (tenantResult.success) {
              sessionStorage.setItem("tenantId", currentUserInfo.tenantId || tenantResult.data.tenantId);
              navigate("/app/dashboard");
            } else {
              message.error(t('Invalid tenant. Please contact support.'));
              handleLocalClear()

            }
          } else {
            navigate("/app/dashboard");
          }
        } else if (currentUserInfo && currentUserInfo?.email) {
          const profileId = currentUserInfo?.userId || currentUserInfo?.id || "";
          if (profileId) {
            localStorage.setItem("userId", profileId);
            localStorage.setItem("id", profileId);
          }
          // Dispatch user info to Redux before navigating
          dispatch(setUserDetail(currentUserInfo));

          // Handle tenant context
          if (urlTenantName) {
            const tenantResult = await setTenant(urlTenantName);
            if (tenantResult.success) {
              sessionStorage.setItem("tenantId", currentUserInfo.tenantId || tenantResult.data.tenantId);
              navigate("/app/dashboard");
            } else {
              message.error(t('Invalid tenant. Please contact support.'));
              handleLocalClear()

            }
          } else {
            navigate("/app/dashboard");
          }
        } else {
          // Logged in, but no profile exists for this email: drop the half-built
          // session so the user stays on login instead of bouncing to dashboard.
          const savedIdentifier = getStoredCompanyIdentifier();
          handleLocalClear()
          if (savedIdentifier) {
            setStoredCompanyIdentifier(savedIdentifier);
          }
          message.error(currentUserInfo?.message || "User not found")
          return
        }
      } else {
        // Login failed - check if it's a tenant mismatch
        if (loginResponse.actualTenantName && normalizeCompanyIdentifier(loginResponse.actualTenantName) !== normalizeCompanyIdentifier(urlTenantName)) {
          const actualTenant = normalizeCompanyIdentifier(loginResponse.actualTenantName);
          message.info(`Redirecting to your tenant: ${actualTenant}`);
          navigate(`/${actualTenant}/login`, { replace: true });
        } else {
          // Preserve companyIdentifier before clearing
          const savedIdentifier = getStoredCompanyIdentifier();
          handleLocalClear()

          if (savedIdentifier) {
            setStoredCompanyIdentifier(savedIdentifier);
          }
          message.error(loginResponse.message);
        }
      }
    } catch (errorInfo) {
      // Preserve companyIdentifier before clearing
      const savedIdentifier = getStoredCompanyIdentifier();
      handleLocalClear();
      if (savedIdentifier) {
        setStoredCompanyIdentifier(savedIdentifier);
      }
      message.error(t('Login failed. Please try again.'));
    }
  }

  const handleSignupClick = async () => {
    let formFields = form.getFieldsValue();
    setCurrentState("register")
    // await sendVerificationEmail(formFields.email)
    setRegisterObj({ email: formFields.email })
    registerForm.setFieldsValue({ email: formFields.email })

  }

  const handlePlan = (plan) => {
    setSelectedPlan(plan)
  }

  const handleTrialStart = async () => {
    showLoader(true)
    let startResponse = await userSignup({ email: userDetail.email, plan: selectedPlan }, "PUT", userDetail.tenantId)
    showLoader(false)
  }


  return (
    <Flex className="layoutStyle" justify="center">
      {
        currentState === "login" ?
          <div className="leftSider" style={{ backgroundImage: "url('/assets/LoginBackground.png')", backgroundRepeat: "no-repeat", maxWidth: "580px" }}>
            <div className="top-logo">
              {companyLogo && !logoError ? (
                <img
                  src={companyLogo}
                  alt={t('Company Logo')}
                  style={{ maxWidth: "176px", maxHeight: "60px" }}
                  onError={() => setLogoError(true)}
                />
              ) : (
                <VeoliaLogo type="small" />
              )}
            </div>
            <div className="middle-section">
              <img src="/assets/LoginImg1.svg" className="top-image" alt="" />
              <img src="/assets/LoginImg2.svg" className="center-image" alt="" />
              <img src="/assets/LoginImg3.svg" alt="" />
            </div>
            <div className="bottom-section">
              <div className="login-title" style={{ color: themeColor }}>{isIdentifierStep ? "Welcome to FlxFlow!" : `Welcome to ${companyName}!`}</div>
              <div className="login-subtitle">{t('A streamlined tool for flexible workflows, automated agents and dynamic form creation with custom logic')}</div>
            </div>
          </div>
          :
          <div className="leftSider" style={{ width: "36rem", flex: "0 0 36rem", backgroundImage: "url('/assets/RegistrationBackground.png')", backgroundRepeat: "no-repeat" }}>
            <div className="top-logo">
              <Flex justify="center" >
                <img src="/assets/LogoWhite.svg" className="cursor-pointer" width={176} />
              </Flex>
            </div>
            <div className="middle-section" style={{ marginLeft: "0", width: "60%" }}>
              <div className="login-title" style={{ color: "#fff" }}>{t("Let's setup your account on FlxFlow")}</div>
              <RegistrationSteps currentStep={currentStep} />
            </div>
            <div className="bottom-section" style={{ width: "60%" }}>
              <div className="login-subtitle cursor-pointer" style={{ color: "#fff" }} onClick={handleBackToLogin}><ArrowLeftOutlined style={{ marginRight: "10px" }} /> {t('Back to Login.')}</div>
            </div>
          </div>
      }
      <div className="rightSider" >
        {
          currentState === "login" ?
            <div className="auth-panel">
              <div className="auth-header">
                <h1 className="auth-title">{isIdentifierStep ? "Welcome to FlxFlow" : `Hello! Welcome to ${companyName}`}</h1>
                {isIdentifierStep && (
                  <p className="auth-subtitle">{t('Enter your company identifier to continue.')}</p>
                )}
              </div>
              <div>
                <Form
                  form={form}
                  onFinish={isIdentifierStep ? handleIdentifierContinue : handleLogin}
                  layout="vertical"
                  className="auth-form"
                  validateMessages="onSubmit"
                  validateTrigger="onSubmit"
                >
                    {isIdentifierStep ? (
                      <Form.Item
                        label={t('Company Identifier')}
                        name="identifier"
                        normalize={normalizeCompanyIdentifier}
                        rules={[
                          { required: true, whitespace: true, message: "Company identifier is required" }
                        ]}
                      >
                        <Input
                          placeholder={t('e.g. my-company')}
                          size="large"
                          className="auth-input"
                          autoFocus
                          style={{ borderColor: themeColor }}
                        />
                      </Form.Item>
                    ) : (
                      <>
                        <Form.Item
                          label={t('Email address')}
                          name="email"
                          rules={[
                            { required: true, whitespace: true, message: "Email is required" },
                            {
                              validator: async (_, value) => {
                                if (!value) return;
                                setUserExists(true)
                                return Promise.resolve();
                              }
                            }
                          ]}
                        >
                          <Input
                            placeholder="email@company.com"
                            size="large"
                            className="auth-input"
                            style={{
                              borderColor: themeColor,
                              borderRadius: 10,
                            }}
                            suffix={userExists ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : null}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('Password')}
                          name="password"
                          rules={[
                            { required: true, whitespace: true, message: "Password is required" }
                          ]}
                        >
                          <Input.Password
                            placeholder={t('Enter Password')}
                            size="large"
                            className="auth-input"
                            style={{ borderRadius: 10 }}
                          />
                        </Form.Item>
                        <div className="auth-forgot">
                          <span
                            className="auth-link"
                            onClick={() => navigate("/forgot-password")}
                          >
                            {t('Forgot password?')}
                          </span>
                        </div>
                      </>
                    )}

                    {/* {userExists && (
                      <>
                        <Form.Item 
                          label={<span className="text-gray-700 font-medium">Password</span>} 
                          name="password"
                        >
                          <Input.Password 
                            placeholder="Enter password" 
                            className="h-12 rounded-lg border-2 border-gray-300 focus:border-blue-600 hover:border-blue-600"
                          />
                        </Form.Item>
                        <div className="text-right">
                          <span className="text-blue-600 text-sm cursor-pointer hover:text-blue-800">
                            Forgot Password?
                          </span>
                        </div>
                      </>
                    )} */}

                    {/* {showLogin && (
                      <Button 
                        size="large" 
                        htmlType="submit" 
                        className="w-full h-12 bg-blue-600 hover:bg-blue-700 border-0 rounded-lg text-white font-medium mt-6"
                        type="primary"
                      >
                        {userExists ? "Login" : "Continue"}
                      </Button>
                    )} */}
                    <Button
                      size="large"
                      htmlType="submit"
                      block
                      className="auth-button"
                      type="primary"
                      style={{ backgroundColor: themeColor }}
                      disabled={!isIdentifierStep && isAuthStateLoading}
                      loading={!isIdentifierStep && isAuthStateLoading}
                    >
                      {isIdentifierStep ? "Continue" : (isAuthStateLoading ? "Loading..." : "Log in")}
                    </Button>
                  </Form>

                  {!isIdentifierStep && showSignup && (
                    <div style={{ marginTop: 16 }}>
                      <Button
                        size="large"
                        block
                        className="auth-button"
                        type="primary"
                        style={{ backgroundColor: themeColor }}
                        onClick={handleSignupClick}
                      >
                        {t('Create new account')}
                      </Button>
                    </div>
                  )}

                  {/* Google Login Section */}
                  {!isIdentifierStep && (
                    <div style={{ marginTop: 24 }}>
                      <Divider>{t('or continue with')}</Divider>
                      <div className="auth-google" style={{
                        pointerEvents: isAuthStateLoading ? 'none' : 'auto',
                        opacity: isAuthStateLoading ? 0.5 : 1
                      }}>
                        <GoogleLogin
                          size="large"
                          onSuccess={responseMessage} onError={errorMessage} />
                      </div>
                    </div>
                  )}
              </div>
            </div>
            :
            <Flex justify="space-between" vertical style={{ width: currentStep === 3 ? "45rem" : "37rem", height: "100%" }}>
              <div style={{ marginTop: "6rem" }}>

                <Form form={registerForm} layout="vertical" {...formItemLayout}>
                  {
                    currentStep === 0 ?
                      <>
                        <div className="bottom-section" style={{ marginBottom: "2rem" }}>
                          <div className="register-title">{t('Create your account')}</div>
                        </div>
                        <div className="login-form" style={{ marginBottom: "3rem" }}>
                          <Form.Item label={t('Email')} name="email" required={true}>
                            <Input placeholder={t('Enter email address')} />
                          </Form.Item>

                        </div>
                        <div>
                          <Button size="large" className="submit-button" style={{ width: "75%", marginBottom: "1rem" }} onClick={(e) => handleStep(e, 1)}>{t('Send verification code')}</Button>
                          {/* <Flex align="center">Already have an account? <div className="login-subtitle cursor-pointer" onClick={() => setCurrentState("login")} style={{ color: "#141BCB", fontSize: "1rem", marginLeft: "5px", fontWeight: "600" }} >Login</div></Flex> */}
                        </div>
                      </>
                      : currentStep === 1 ?
                        <>
                          <div className="bottom-section" style={{ marginBottom: "2rem" }}>
                            <div className="register-title">{t('Email verification')}</div>
                          </div>
                          <img src="/assets/VerifyEmail.svg" />
                          <div className="login-form" style={{ marginBottom: "1.5rem", width: "75%" }}>
                            <div style={{ marginBottom: "12px" }}>{t('We have send an email to')} <b>{registerObj.email}</b> {t('to verify your email address. Please click on the verification link to continue.')}</div>

                          </div>
                          <div>
                            <Flex align="center">{t("Didn't receive an email?")} <div className="login-subtitle cursor-pointer" style={{ color: "#141BCB", fontSize: "1rem", marginLeft: "5px", fontWeight: "600" }} onClickCapture={() => sendVerificationEmail(registerObj.email)}>{t('Resend')}</div></Flex>
                          </div>
                        </>
                        : currentStep === 2 ?
                          <>
                            <div className="bottom-section" style={{ marginBottom: "2rem" }}>
                              <div className="register-title">{t('Enter your account details')}</div>
                            </div>
                            <div className="login-form" style={{ marginBottom: "3rem" }}>
                              <Form.Item label={t('Email')} name="email">
                                <Input placeholder={t('Enter email address')} readOnly suffix={<CheckCircleFilled style={{ color: "#50A248", marginRight: "2px" }} />} />
                              </Form.Item>
                              <Flex style={{ width: "75%", gap: 16 }}>
                                <Form.Item label={t('First Name')} name="firstName" rules={[{ required: true, whitespace: true, message: "First name is required" }]} style={{ width: "100%" }} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                  <Input placeholder={t('Enter first name')} />
                                </Form.Item>
                                <Form.Item label={t('Last Name')} name="lastName" rules={[{ required: true, whitespace: true, message: "Last name is required" }]} style={{ width: "100%" }} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                  <Input placeholder={t('Enter last name')} />
                                </Form.Item>
                              </Flex>

                              <Form.Item label={t('Password')} name="password" help="Password must be 8 characters or longer" rules={[{ required: true, whitespace: true, message: "Password is required" }]}>
                                <Input.Password placeholder={t('Enter password')} />
                              </Form.Item>
                              <Form.Item label="" className="agreement-check" name="agree" wrapperCol={{ span: 20, marginTop: "20px" }}>
                                <Checkbox style={{ fontSize: "12px" }}>{t('By signing up, I agree to the FlxFlow')} <a>{t('Privacy Policy')}</a> and <a>{t('Terms & Conditions')}</a></Checkbox>
                              </Form.Item>
                            </div>
                            <div>
                              <Button size="large" className="submit-button" style={{ width: "75%", marginBottom: "1rem" }} onClick={(e) => handleStep(e, 3)}>{t('Continue')}</Button>
                            </div>
                          </>
                          :
                          <>
                            <div className="bottom-section" style={{ marginBottom: "2rem", maxWidth: "100%" }}>
                              <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                                <div className="register-title">{t('Choose your plan')}</div>
                                <Button style={{ borderColor: "#C7C8F3" }}><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Compare all plan features')}</Button>
                              </Flex>
                            </div>
                            <Flex gap={32}>
                              <div className={`${selectedPlan === "standard" ? 'selected-plan' : ""} plan-card`} onClick={() => handlePlan('standard')}>
                                <div className="plan-icon">

                                  <img src="/assets/StandardPlan.svg" width={28} />
                                </div>
                                <div className="plan-title">{t('Standard')}</div>
                                <div className="plan-features">
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Spaces Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Master Data Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>

                                </div>
                                <div className="plan-price">
                                  <b style={{ fontSize: "1.375rem" }}>$ 10/</b>month
                                </div>

                              </div>
                              <div className={`${selectedPlan === "pro" ? 'selected-plan' : ""} plan-card`} onClick={() => handlePlan('pro')}>
                                <div className="plan-icon">

                                  <img src="/assets/ProPlan.svg" width={24} />
                                </div>
                                <div className="plan-title">{t('Pro')}</div>
                                <div className="plan-features">
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Spaces Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Master Data Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Spaces Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Master Data Creation</div>

                                </div>
                                <div className="plan-price">
                                  <b style={{ fontSize: "1.375rem" }}>$ 20/</b>month
                                </div>

                              </div>
                              <div className={`${selectedPlan === "enterprise" ? 'selected-plan' : ""} plan-card`} onClick={() => handlePlan('enterprise')}>
                                <div className="plan-icon">
                                  <img src="/assets/EnterprisePlan.svg" width={34} />

                                </div>
                                <div className="plan-title">{t('Enterprise')}</div>
                                <div className="plan-features">
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Spaces Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Master Data Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />3 Master Data Creation</div>
                                  <div className="feature"><CheckCircleOutlined style={{ marginRight: "3px", color: "#3E44D4" }} />{t('Add 6 workflow steps')}</div>
                                </div>
                                <div className="plan-price">
                                  <b style={{ fontSize: "1.375rem" }}>{t('Contact Us')}</b>
                                </div>
                              </div>
                            </Flex>
                            <div>
                              <Button size="large" className="submit-button" style={{ width: "100%", marginBottom: "1rem", marginTop: "2rem" }} onClick={handleTrialStart}>{t('Start Trial')}</Button>
                            </div>
                          </>
                  }
                </Form>
              </div>


            </Flex>
        }

      </div>
    </Flex>
  );
};

export default LoginPage;
